import { NarrativeCommunityHeader } from '../NarrativeCommunityHeader';
import { NarrativeCommunityList } from '../NarrativeCommunityList';
import { useMemo } from 'react';
import { useNarrativeCommunityCompany } from './hooks/useNarrativeCommunityCompany';

import { createCompanyCanonicalUrl, getNarrativeCommunityMeta } from './utils';
import { findPrimaryIndustryBySlug } from '../../utils/findPrimaryIndustryBySlug';
import { NarrativeCommunityCompanySearch } from '../NarrativeCommunityCompanySearch';
import { Button } from '@simplywallst/ui-core/next';
import { useNarrativeCommunitySort } from './hooks/useNarrativeCommunitySort';

type Props = {
  primaryIndustrySlug: string | undefined | null;
  companyNameSlug: string | undefined | null;
  exchangeTicker: string | undefined | null;
  market: string | undefined | null;
};
export const NarrativeCommunity = ({
  primaryIndustrySlug,
  companyNameSlug,
  exchangeTicker,
  market,
}: Props) => {
  const { primaryIndustryId, primaryIndustryLabel } = useMemo(
    () => findPrimaryIndustryBySlug(primaryIndustrySlug),
    [primaryIndustrySlug]
  );

  const companyCanonicalUrl = createCompanyCanonicalUrl({
    primaryIndustrySlug,
    companyNameSlug,
    exchangeTicker,
    market,
  });

  const { data: companyResponse, isLoading } = useNarrativeCommunityCompany(
    {
      canonicalUrl: companyCanonicalUrl!,
    },
    {
      enabled: !!companyCanonicalUrl,
    }
  );

  const {
    id: companyId,
    name: companyName,
    uniqueSymbol,
    media,
  } = companyResponse?.Company ?? {};

  const { seoTitle, seoDescription, seoImageUrl } = getNarrativeCommunityMeta({
    companyName,
    uniqueSymbol,
    coverImageUrl: media?.find((m) => m.transform === 'main-header')?.url,
    primaryIndustryLabel,
    market,
  });

  const { sort, sortHash, changeSort } = useNarrativeCommunitySort();

  return (
    <>
      <NarrativeCommunityHeader
        primaryIndustryLabel={primaryIndustryLabel}
        companyNameSlug={companyNameSlug}
        market={market}
        primaryIndustrySlug={primaryIndustrySlug}
        exchangeTicker={exchangeTicker}
        companyName={companyName}
        companyId={companyId}
        isLoading={isLoading}
      />

      <div className="grid grid-cols-[auto_max-content] items-center gap-x0_5 justify-between">
        <NarrativeCommunityCompanySearch companyName={companyName} />
        <div className="grid gap-x0_5 grid-cols-[repeat(2,max-content)]">
          <Button
            type="button"
            size="medium"
            variant={sortHash === 'undervalued' ? 'secondary' : 'outline'}
            onClick={() =>
              changeSort(sortHash === 'undervalued' ? null : 'undervalued')
            }
          >
            Undervalued
          </Button>
          <Button
            type="button"
            size="medium"
            variant={sortHash === 'overvalued' ? 'secondary' : 'outline'}
            onClick={() =>
              changeSort(sortHash === 'overvalued' ? null : 'overvalued')
            }
          >
            Overvalued
          </Button>
        </div>
      </div>

      <NarrativeCommunityList
        filter={{
          market,
          primaryIndustryId,
          canonicalUrl: companyCanonicalUrl,
        }}
        sort={sort}
        seoTitle={seoTitle}
        seoDescription={seoDescription}
        seoImageUrl={seoImageUrl}
      />
    </>
  );
};
